<script lang="ts">
  import { onMount } from "svelte"
  import { Info, SquarePlus, CircleEllipsis, Copy, Trash2 } from "lucide-svelte"
  import Dropdown from "$app/components/Dropdown.svelte"
  import Expanded from "./Expanded.svelte"
  import AnnualGoal from "./AnnualGoal.svelte"
  import NewGoal from "./NewGoal.svelte"
  import Modal from "$app/components/Modal.svelte"
  import { setSortable, duplicateGoal, deleteGoal } from "./methods"
  import { appStore } from "$app/store.svelte"
  import { wait } from "$app/utils"
  import { slide } from "svelte/transition"

  type AddGoalType = {
    type: "AnnualGoal"
    parent_goal_id: number
    parent_goal_type: "LongTermGoal"
    expand?: boolean
  }
  interface Props {
    goal: GoalType
    pos?: number
    expanded?: boolean
    onadd?: (e: AddGoalType) => void
  }

  let { goal, pos = 1, expanded = $bindable(false) }: Props = $props()
  let dragOver = $state(false)

  let openGoalDropdown = $state(false)
  let sortableSelection: HTMLElement = $state()
  let selected = $state(false)
  let openNewGoalModal = $state(false)
  let subGoal: GoalType = $state({} as GoalType)

  function addSubGoal(): void {
    subGoal = {
      type: "AnnualGoal",
      parent_goal_id: goal.id,
      parent_goal_type: "LongTermGoal"
    }
    openNewGoalModal = true
  }

  let mounted = $state(false)
  $effect(() => {
    if (mounted && expanded) {
      setTimeout(() => {
        setSortable(sortableSelection, "AnnualGoal")
      }, 50)
    }
  })

  onMount(() => (mounted = true))
</script>

<div class="relative pr-6">
  <div
    class="border-l-4 border-long-term-goal shadow-sm flex w-full ml-6 items-center relative select-none"
    class:border-2={dragOver}
    class:border-green-500={dragOver}
    class:bg-blue-100={selected}
    role="button"
    tabindex="0"
    ondragover={async (e) => {
      await wait(50)
      dragOver = appStore.draggedElement.type === "AnnualGoal"
    }}
    ondragleave={async () => {
      await wait(100)
      dragOver = false
    }}
    ondrop={async (e) => {
      if (appStore.draggedElement.type === "AnnualGoal") {
        console.log("DROP Annual")
        appStore.dropElement = {
          ...appStore.dropElement,
          type: "LongTermGoal",
          id: goal.id,
          goal_id: 0
        }
      }
      await wait(50)

      dragOver = false
    }}
    ondblclick={() =>
      document.dispatchEvent(
        new CustomEvent("show-goal-detail", { detail: { goal } })
      )}
  >
    <div class="p-2 flex pl-4 border-t border-b border-gray-300 w-full">
      {#if appStore.goals.filter((g) => g.parent_goal_id === goal.id).length > 0}
        <div class="absolute top-6" style="left:-14px">
          <Expanded
            class="text-long-term-goal"
            bind:expanded
            data={{ name: "goal-expander" }}
          />
        </div>
      {/if}
      <div class="w-11/12 text-left">
        <p class="text-ellipsis overflow-hidden">
          <span class="text-gray-400 text-sm">{pos}.</span>
          <span class="font-bold">{goal.name}</span>
        </p>
        <p class="text-xs text-gray-500">Long Term Goal</p>
      </div>
      <div class="w-1/12 text-center flex items-center justify-center">
        <Dropdown bind:open={openGoalDropdown}>
          {#snippet button()}
            <span
              class="rounded-full flex items-center justify-center focus:outline-none"
            >
              <CircleEllipsis class="text-gray-400 w-7 h-7" />
            </span>
          {/snippet}

          <div>
            <div
              class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-core border focus:outline-none z-10"
              role="menu"
            >
              <div class="py-1" role="none">
                <button
                  onclick={() =>
                    document.dispatchEvent(
                      new CustomEvent("show-goal-detail", { detail: { goal } })
                    )}
                  class="btn-dropdown"
                  role="menuitem"
                >
                  <Info class="text-gray-400 w-4 h-4 mr-3" />
                  See Details</button
                >
                {#if appStore.editable}
                  <button
                    onclick={() => addSubGoal()}
                    class="btn-dropdown"
                    role="menuitem"
                    type="button"
                  >
                    <SquarePlus class="text-gray-400 w-4 h-4 mr-3" />
                    Add Sub-Goal
                  </button>
                  <button
                    class="btn-dropdown"
                    role="menuitem"
                    onclick={() => {
                      openGoalDropdown = false
                      duplicateGoal(goal)
                    }}
                  >
                    <Copy class="text-gray-400 w-4 h-4 mr-3" />
                    Duplicate
                  </button>
                  <button
                    class="btn-dropdown"
                    role="menuitem"
                    onclick={async () => {
                      selected = true
                      await deleteGoal(goal)
                      openGoalDropdown = false
                      selected = false
                    }}
                  >
                    <Trash2 class="text-gray-400 w-4 h-4 mr-3" />
                    Delete
                  </button>
                {/if}
              </div>
            </div>
          </div>
        </Dropdown>
      </div>
    </div>
  </div>

  {#if expanded}
    <div
      class="pl-4"
      bind:this={sortableSelection}
      data-type="LongTermGoal"
      data-id={goal.id}
      transition:slide
      role="button"
      tabindex="0"
      ondblclick={() => {
        document.dispatchEvent(
          new CustomEvent("show-goal-detail", { detail: { goal } })
        )
      }}
    >
      {#each appStore.goals
        .filter((g) => g.parent_goal_id === goal.id)
        .sort((a, b) => a.parent_position - b.parent_position) as item, idx (item.id)}
        <div class="ml-2" data-id={item.id} data-type="AnnualGoal">
          <AnnualGoal goal={item} pos={idx + 1} {expanded} />
        </div>
      {/each}
    </div>
  {/if}
</div>

<Modal bind:open={openNewGoalModal}>
  <NewGoal
    goal={subGoal}
    disableParent={true}
    disableType={true}
    oncreate={(g) => (openNewGoalModal = false)}
  />
</Modal>
